import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X } from "lucide-react";
import { getConsent } from "@/components/CookieConsent";
import { useI18n } from "@/i18n";

// Install invite rules: never on a first load (a visitor who has seen nothing has no reason
// to install), a dismissal is remembered for 30 days, and nothing ever shows when the app is
// already running standalone.
const VISITS_KEY = "rr_visits";
const DISMISSED_KEY = "rr_install_dismissed_at";
const SNOOZE_DAYS = 30;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIosSafari = () => {
  const ua = window.navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && "ontouchend" in document);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
};

const snoozed = () => {
  const at = Number(localStorage.getItem(DISMISSED_KEY) || 0);
  return at > 0 && Date.now() - at < SNOOZE_DAYS * 86400000;
};

export default function InstallPrompt() {
  const { t } = useI18n();
  const location = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const visits = Number(localStorage.getItem(VISITS_KEY) || 0) + 1;
    localStorage.setItem(VISITS_KEY, String(visits));

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIosSafari() && !snoozed() && visits >= 2) setIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  // Earned attention only: a second visit, or a visitor who opened a player page. The
  // cookie banner owns the bottom of the screen first — two stacked bars would trap taps.
  useEffect(() => {
    if (isStandalone() || snoozed() || !getConsent()) return;
    const visits = Number(localStorage.getItem(VISITS_KEY) || 0);
    const onPlayerPage = location.pathname.startsWith("/players/");
    if (deferred && (visits >= 2 || onPlayerPage)) setVisible(true);
    if (iosHint && (visits >= 2 || onPlayerPage)) setVisible(true);
  }, [deferred, iosHint, location.pathname]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  }, [deferred]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-[#B24BFF]/30 bg-[#0B0E14]/95 px-4 py-3 backdrop-blur-md"
      data-testid="install-prompt"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <img src="/favicon.svg" alt="" className="h-8 w-8 shrink-0" />
        <p className="flex-1 font-mono2 text-[11px] leading-snug text-slate-200">
          {t("pwa.invite")}
          {iosHint && !deferred && (
            <span className="mt-0.5 flex items-center gap-1 text-[10px] text-[#B24BFF]" data-testid="install-ios-hint">
              <Share className="h-3 w-3" /> {t("pwa.ios_steps")}
            </span>
          )}
        </p>
        {deferred && (
          <button
            onClick={install}
            data-testid="install-accept-button"
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#B24BFF] px-4 py-2 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#0B0E14] transition-shadow hover:shadow-[0_0_18px_rgba(178,75,255,0.55)]"
          >
            <Download className="h-3.5 w-3.5" /> {t("pwa.install")}
          </button>
        )}
        <button
          onClick={dismiss}
          aria-label={t("pwa.dismiss")}
          data-testid="install-dismiss-button"
          className="shrink-0 rounded-full p-2 text-slate-500 transition-colors hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
